<script setup>
import { computed, onMounted, ref, reactive, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

// Guardrails state
const guardrails = ref([]);

// Dialog state
const guardrailDialogRef = ref(null);
const editingGuardrail = ref(null);

// Guardrail types available - organized by category
const guardrailTypes = [
  // === INPUT VALIDATION ===
  {
    type: 'max_length',
    name: 'Limite de Caracteres',
    description: 'Limita o tamanho maximo da mensagem de entrada',
    icon: 'i-lucide-text-cursor-input',
    valueType: 'number',
    placeholder: '10000',
    defaultValue: 10000,
    category: 'input',
  },
  {
    type: 'min_length',
    name: 'Tamanho Minimo',
    description: 'Exige um tamanho minimo para mensagens',
    icon: 'i-lucide-text-cursor',
    valueType: 'number',
    placeholder: '5',
    defaultValue: 5,
    category: 'input',
  },
  {
    type: 'block_keywords',
    name: 'Bloquear Palavras',
    description: 'Bloqueia mensagens que contem palavras especificas',
    icon: 'i-lucide-ban',
    valueType: 'array',
    placeholder: 'palavra1, palavra2, palavra3',
    defaultValue: [],
    category: 'input',
  },
  {
    type: 'require_keywords',
    name: 'Exigir Palavras-chave',
    description: 'Exige que a mensagem contenha pelo menos uma das palavras',
    icon: 'i-lucide-check-circle',
    valueType: 'array',
    placeholder: 'suporte, ajuda, duvida',
    defaultValue: [],
    category: 'input',
  },
  {
    type: 'regex_block',
    name: 'Bloquear por Regex',
    description: 'Bloqueia mensagens que correspondam a padroes regex',
    icon: 'i-lucide-regex',
    valueType: 'array',
    placeholder: '\\d{11}, \\b[A-Z]{3}\\d{4}\\b',
    defaultValue: [],
    category: 'input',
  },
  {
    type: 'block_urls',
    name: 'Bloquear URLs',
    description: 'Bloqueia mensagens que contenham links/URLs',
    icon: 'i-lucide-link-2-off',
    valueType: 'boolean',
    defaultValue: true,
    category: 'input',
  },
  {
    type: 'block_code',
    name: 'Bloquear Codigo',
    description: 'Bloqueia mensagens que contenham codigo de programacao',
    icon: 'i-lucide-code-2',
    valueType: 'boolean',
    defaultValue: true,
    category: 'input',
  },

  // === SECURITY ===
  {
    type: 'prompt_injection_filter',
    name: 'Protecao contra Prompt Injection',
    description:
      'Detecta e bloqueia tentativas de manipular o comportamento do agente',
    icon: 'i-lucide-shield-ban',
    valueType: 'boolean',
    defaultValue: true,
    category: 'security',
  },
  {
    type: 'dangerous_content_filter',
    name: 'Filtro de Conteudo Perigoso',
    description:
      'Bloqueia conteudo relacionado a armas, drogas, hacking, ilegalidades',
    icon: 'i-lucide-alert-triangle',
    valueType: 'boolean',
    defaultValue: true,
    category: 'security',
  },
  {
    type: 'language_filter',
    name: 'Filtro de Linguagem',
    description: 'Bloqueia linguagem ofensiva, palavroes e conteudo toxico',
    icon: 'i-lucide-shield-alert',
    valueType: 'boolean',
    defaultValue: true,
    category: 'security',
  },
  {
    type: 'pii_filter',
    name: 'Filtro de PII',
    description:
      'Detecta e protege informacoes pessoais (CPF, CNPJ, email, telefone, cartoes)',
    icon: 'i-lucide-user-x',
    valueType: 'boolean',
    defaultValue: true,
    category: 'security',
  },
  {
    type: 'topic_restriction',
    name: 'Restricao de Topico',
    description: 'Restringe as conversas a topicos especificos permitidos',
    icon: 'i-lucide-bookmark',
    valueType: 'array',
    placeholder: 'vendas, suporte, produtos',
    defaultValue: [],
    category: 'security',
  },
  {
    type: 'rate_limit',
    name: 'Limite de Requisicoes',
    description:
      'Limita o numero de mensagens por sessao para evitar abusos (ex: 10/60 = 10 msgs por 60 segundos)',
    icon: 'i-lucide-timer',
    valueType: 'string',
    placeholder: '10/60',
    defaultValue: '10/60',
    category: 'security',
  },

  // === OUTPUT CONTROL ===
  {
    type: 'block_output_keywords',
    name: 'Filtrar Palavras na Resposta',
    description:
      'Remove ou bloqueia palavras sensiveis nas respostas do agente',
    icon: 'i-lucide-eye-off',
    valueType: 'array',
    placeholder: 'segredo, senha, confidencial',
    defaultValue: [],
    category: 'output',
  },
  {
    type: 'max_tokens',
    name: 'Limite de Tokens',
    description: 'Limita o numero de tokens na resposta do agente',
    icon: 'i-lucide-hash',
    valueType: 'number',
    placeholder: '1000',
    defaultValue: 1000,
    category: 'output',
  },
  {
    type: 'response_format',
    name: 'Formato de Resposta',
    description:
      'Valida se a resposta segue um formato especifico (json, markdown, plain)',
    icon: 'i-lucide-file-json',
    valueType: 'select',
    options: ['json', 'markdown', 'plain'],
    defaultValue: 'plain',
    category: 'output',
  },
];

const getGuardrailInfo = type => {
  return guardrailTypes.find(g => g.type === type) || {};
};

const isGuardrailConfigured = type => {
  return guardrails.value.some(g => g.type === type);
};

// ComboBox options for guardrail type selector
const guardrailTypeOptions = computed(() =>
  guardrailTypes.map(g => ({
    value: g.type,
    label: `${g.name}${isGuardrailConfigured(g.type) ? ' (configurado)' : ''}`,
  }))
);

// ComboBox options for response format
const responseFormatOptions = [
  { value: 'json', label: 'JSON' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'plain', label: 'Plain Text' },
];

// Form state
const guardrailForm = reactive({
  type: '',
  value: '',
  enabled: true,
});

const updateAgentFromData = agentData => {
  if (!agentData) return;
  guardrails.value = agentData.guardrails || [];
};

onMounted(() => {
  if (agent.value) {
    updateAgentFromData(agent.value);
  }
});

watch(
  agent,
  newAgent => {
    if (newAgent) updateAgentFromData(newAgent);
  },
  { deep: false }
);

const getConfiguredGuardrail = type => {
  return guardrails.value.find(g => g.type === type);
};

const formatValue = guardrail => {
  const info = getGuardrailInfo(guardrail.type);
  if (info.valueType === 'array') {
    return Array.isArray(guardrail.value)
      ? guardrail.value.slice(0, 3).join(', ') +
          (guardrail.value.length > 3 ? ` +${guardrail.value.length - 3}` : '')
      : guardrail.value;
  }
  if (info.valueType === 'boolean') {
    return guardrail.value ? 'Ativado' : 'Desativado';
  }
  if (info.valueType === 'string' && guardrail.type === 'rate_limit') {
    const parts = String(guardrail.value).split('/');
    if (parts.length === 2) {
      return `${parts[0]} msgs / ${parts[1]}s`;
    }
  }
  if (info.valueType === 'select') {
    return `Formato: ${guardrail.value}`;
  }
  return guardrail.value;
};

const openAddDialog = (type = null) => {
  if (type) {
    const existing = getConfiguredGuardrail(type);
    const typeInfo = getGuardrailInfo(type);

    if (existing) {
      editingGuardrail.value = existing;
      guardrailForm.type = existing.type;
      guardrailForm.enabled = existing.enabled !== false;

      if (typeInfo.valueType === 'array') {
        guardrailForm.value = Array.isArray(existing.value)
          ? existing.value.join(', ')
          : existing.value || '';
      } else {
        guardrailForm.value = existing.value;
      }
    } else {
      editingGuardrail.value = null;
      guardrailForm.type = type;
      guardrailForm.value = typeInfo.defaultValue;
      guardrailForm.enabled = true;
    }
  } else {
    editingGuardrail.value = null;
    guardrailForm.type = '';
    guardrailForm.value = '';
    guardrailForm.enabled = true;
  }
  guardrailDialogRef.value?.open();
};

const saveGuardrail = async () => {
  try {
    const typeInfo = getGuardrailInfo(guardrailForm.type);

    let value = guardrailForm.value;
    if (typeInfo.valueType === 'array' && typeof value === 'string') {
      value = value
        .split(',')
        .map(s => s.trim())
        .filter(s => s);
    } else if (typeInfo.valueType === 'number') {
      value = parseInt(value, 10);
    }

    const guardrailData = {
      type: guardrailForm.type,
      value,
      enabled: guardrailForm.enabled,
    };

    let newGuardrails;

    if (editingGuardrail.value) {
      newGuardrails = guardrails.value.map(g => {
        if (g.type === editingGuardrail.value.type) {
          return guardrailData;
        }
        return g;
      });
    } else {
      if (isGuardrailConfigured(guardrailForm.type)) {
        useAlert('Este guardrail ja esta configurado');
        return;
      }
      newGuardrails = [...guardrails.value, guardrailData];
    }

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      guardrails: newGuardrails,
    });

    guardrails.value = newGuardrails;
    guardrailDialogRef.value?.close();
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('Guardrail salvo e sincronizado');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar guardrail');
  }
};

const toggleGuardrail = async guardrail => {
  if (!props.agentId) return;

  try {
    const newGuardrails = guardrails.value.map(g => {
      if (g.type === guardrail.type) {
        return { ...g, enabled: !g.enabled };
      }
      return g;
    });

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      guardrails: newGuardrails,
    });

    guardrails.value = newGuardrails;
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    useAlert(`Guardrail ${guardrail.enabled ? 'desativado' : 'ativado'}`);
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar guardrail');
  }
};

const removeGuardrail = async guardrail => {
  if (!props.agentId) return;

  try {
    const newGuardrails = guardrails.value.filter(
      g => g.type !== guardrail.type
    );

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      guardrails: newGuardrails,
    });

    guardrails.value = newGuardrails;
    useAlert('Guardrail removido');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover guardrail');
  }
};

const selectedTypeInfo = computed(() => {
  return getGuardrailInfo(guardrailForm.type);
});

const getCategoryColor = type => {
  const typeInfo = getGuardrailInfo(type);
  const categoryColors = {
    input: 'bg-blue-100 text-blue-700',
    security: 'bg-red-100 text-red-700',
    output: 'bg-purple-100 text-purple-700',
  };
  return categoryColors[typeInfo.category] || 'bg-gray-100 text-gray-700';
};

// Group guardrails by category
const guardrailsByCategory = computed(() => {
  return {
    input: guardrailTypes.filter(g => g.category === 'input'),
    security: guardrailTypes.filter(g => g.category === 'security'),
    output: guardrailTypes.filter(g => g.category === 'output'),
  };
});

const categoryNames = {
  input: 'Validacao de Entrada',
  security: 'Seguranca',
  output: 'Controle de Saida',
};

const categoryIcons = {
  input: 'i-lucide-log-in',
  security: 'i-lucide-shield',
  output: 'i-lucide-log-out',
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <SettingsHeader
        heading="Guardrails de Seguranca"
        description="Configure regras de seguranca para proteger as interacoes do agente."
      />
      <Button
        label="Adicionar Guardrail"
        icon="i-lucide-shield-plus"
        @click="openAddDialog()"
      />
    </div>

    <!-- Agent Info -->
    <div
      v-if="agent"
      class="mb-6 p-4 bg-n-alpha-1 border border-n-weak rounded-xl"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-bot text-xl text-n-slate-11" />
        </div>
        <div>
          <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
          <p class="text-xs text-n-slate-10">
            {{ guardrails.filter(g => g.enabled !== false).length }}
            guardrails ativos
          </p>
        </div>
      </div>
    </div>

    <!-- Configured Guardrails -->
    <div class="mb-8">
      <h3
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-shield-check text-n-slate-11" />
        Guardrails Configurados
      </h3>

      <div
        v-if="guardrails.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
      >
        <div
          class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-shield text-2xl text-n-slate-10" />
        </div>
        <h4 class="text-md font-medium text-n-slate-12 mb-1">
          Nenhum guardrail configurado
        </h4>
        <p class="text-sm text-n-slate-11 mb-4 max-w-md">
          Adicione guardrails para proteger as interacoes do seu agente.
        </p>
        <Button
          label="Adicionar Guardrail"
          icon="i-lucide-plus"
          variant="secondary"
          @click="openAddDialog()"
        />
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="guardrail in guardrails"
          :key="guardrail.type"
          class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl"
          :class="{ 'opacity-50': guardrail.enabled === false }"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center"
              :class="getCategoryColor(guardrail.type)"
            >
              <span
                class="text-xl"
                :class="[getGuardrailInfo(guardrail.type).icon]"
              />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <p class="font-medium text-n-slate-12">
                  {{ getGuardrailInfo(guardrail.type).name }}
                </p>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ formatValue(guardrail) }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Switch
              :model-value="guardrail.enabled !== false"
              @change="toggleGuardrail(guardrail)"
            />
            <Button
              icon="i-lucide-settings"
              variant="ghost"
              size="sm"
              @click="openAddDialog(guardrail.type)"
            />
            <ConfirmButton
              icon="i-lucide-trash-2"
              variant="ghost"
              size="sm"
              color="slate"
              confirm-color="ruby"
              label=""
              confirm-label=""
              confirm-hint="Confirmar?"
              @click="removeGuardrail(guardrail)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Available Guardrails by Category -->
    <div class="mb-8 space-y-6">
      <div
        v-for="(types, category) in guardrailsByCategory"
        :key="category"
        class="space-y-3"
      >
        <h3
          class="text-md font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span :class="categoryIcons[category]" class="text-n-slate-11" />
          {{ categoryNames[category] }}
          <span class="text-xs font-normal text-n-slate-10">
            ({{ types.filter(t => isGuardrailConfigured(t.type)).length }}/{{
              types.length
            }}
            configurados)
          </span>
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            v-for="type in types"
            :key="type.type"
            class="flex items-center gap-3 p-4 rounded-xl border text-left transition-colors"
            :class="
              isGuardrailConfigured(type.type)
                ? 'border-green-300 bg-green-50'
                : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
            "
            @click="openAddDialog(type.type)"
          >
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center"
              :class="getCategoryColor(type.type)"
            >
              <span class="text-xl" :class="[type.icon]" />
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <p class="font-medium text-n-slate-12">{{ type.name }}</p>
                <span
                  v-if="isGuardrailConfigured(type.type)"
                  class="i-lucide-check text-green-600"
                />
              </div>
              <p class="text-xs text-n-slate-10">{{ type.description }}</p>
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- Info Card -->
    <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <div class="flex gap-3">
        <span
          class="i-lucide-shield-alert text-amber-600 text-xl flex-shrink-0"
        />
        <div>
          <h4 class="font-medium text-amber-900 mb-1">Sobre os Guardrails</h4>
          <p class="text-sm text-amber-700 mb-2">
            Guardrails sao regras de seguranca que protegem as interacoes do seu
            agente de IA. Eles funcionam em tres niveis:
          </p>
          <ul class="text-sm text-amber-700 list-disc ml-4 space-y-1">
            <li>
              <strong>Validacao de Entrada:</strong> Filtra mensagens do usuario
              antes de chegar ao agente
            </li>
            <li>
              <strong>Seguranca:</strong> Protege contra ataques (prompt
              injection), vazamento de dados (PII) e conteudo perigoso
            </li>
            <li>
              <strong>Controle de Saida:</strong> Valida as respostas do agente
              antes de enviar ao usuario
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit Guardrail Dialog -->
  <Dialog
    ref="guardrailDialogRef"
    :title="(editingGuardrail ? 'Editar' : 'Adicionar') + ' Guardrail'"
    description="Configure as regras de seguranca para o agente."
    confirm-button-label="Salvar"
    cancel-button-label="Cancelar"
    :disable-confirm-button="!guardrailForm.type"
    :is-loading="isUpdating"
    overflow-y-auto
    width="lg"
    @confirm="saveGuardrail"
  >
    <div class="space-y-4">
      <!-- Type Selection -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Tipo de Guardrail
        </label>
        <ComboBox
          v-model="guardrailForm.type"
          :options="guardrailTypeOptions"
          :disabled="!!editingGuardrail"
          placeholder="Selecione um tipo"
          search-placeholder="Buscar guardrail..."
        />
      </div>

      <!-- Type Description -->
      <div
        v-if="selectedTypeInfo.description"
        class="p-3 bg-n-alpha-1 rounded-lg"
      >
        <p class="text-sm text-n-slate-11">
          {{ selectedTypeInfo.description }}
        </p>
      </div>

      <!-- Value Input -->
      <template v-if="guardrailForm.type">
        <div v-if="selectedTypeInfo.valueType === 'number'">
          <Input
            v-model="guardrailForm.value"
            type="number"
            label="Valor"
            :placeholder="selectedTypeInfo.placeholder"
          />
        </div>

        <div v-else-if="selectedTypeInfo.valueType === 'array'">
          <TextArea
            v-model="guardrailForm.value"
            label="Valores (separados por virgula)"
            :placeholder="selectedTypeInfo.placeholder"
            auto-height
          />
        </div>

        <div
          v-else-if="selectedTypeInfo.valueType === 'boolean'"
          class="flex items-center gap-2"
        >
          <label class="text-sm font-medium text-n-slate-12"> Status </label>
          <span class="text-sm text-n-slate-11">
            Este guardrail sera
            {{ guardrailForm.enabled ? 'ativado' : 'desativado' }}
          </span>
        </div>

        <div v-else-if="selectedTypeInfo.valueType === 'string'">
          <Input
            v-model="guardrailForm.value"
            label="Valor"
            :placeholder="selectedTypeInfo.placeholder"
          />
        </div>

        <div
          v-else-if="selectedTypeInfo.valueType === 'select'"
          class="flex flex-col gap-2"
        >
          <label class="text-sm font-medium text-n-slate-12">
            Selecione uma opcao
          </label>
          <ComboBox
            v-model="guardrailForm.value"
            :options="responseFormatOptions"
            placeholder="Selecione..."
          />
        </div>

        <div class="flex items-center gap-3">
          <Switch v-model="guardrailForm.enabled" />
          <span class="text-sm text-n-slate-12">Guardrail habilitado</span>
        </div>
      </template>
    </div>
  </Dialog>
</template>
